import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { FC, useState } from 'react';
import { UserUtils } from '@/features/superAdmin/features/users/utils/UserUtils';
import { TenantRoles } from '@/@types/auth.types';
import { useTranslation } from 'react-i18next';

type Props = {
    roleId?: string;
    onChange: (roleId: string) => void;
};

const UserTenantRolesPicker: FC<Props> = ({ roleId, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    const { t } = useTranslation();

    const isOpenToggler = () => {
        setIsOpen((prev) => !prev);
    };

    const role = UserUtils.getUserRole(roleId);

    const tenantRoles = Object.entries(TenantRoles);

    return (
        <DropdownMenu open={isOpen} onOpenChange={isOpenToggler}>
            <DropdownMenuTrigger
                asChild
                className={cn(
                    'w-full px-4 py-3 flex rounded-semi-xl',
                    isOpen ? 'border-popover-1000' : '',
                )}
            >
                <Button variant="outline" className="w-40 hover:shadow-none">
                    <span className="text-xs text-popover-1000 truncate">
                        {role ? role : 'Role is not assigned'}
                    </span>
                    <div
                        className={cn(
                            'transition-transform duration-50 ease-in-out ml-auto',
                            isOpen ? '-rotate-180' : 'rotate-0',
                        )}
                    >
                        {<ArrowDownIcon />}
                    </div>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className={
                    'w-(--radix-dropdown-menu-trigger-width) h-fit max-h-50 p-0 rounded-semi-xl border border-popover-100 bg-popover-10 overflow-y-scroll no-scrollbar'
                }
            >
                <DropdownMenuCheckboxItem
                    key="unassigned"
                    className="px-3.5 py-3"
                    onCheckedChange={() => onChange('')}
                >
                    {t('Role is not assigned')}
                </DropdownMenuCheckboxItem>
                {tenantRoles.map(([name, id]) => (
                    <DropdownMenuCheckboxItem
                        key={name}
                        className="px-3.5 py-3"
                        checked={name === role}
                        onCheckedChange={() => onChange(id)}
                    >
                        {name}
                    </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default UserTenantRolesPicker;
